import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { AVAILABLE_MAP_ZONES } from './map/constants';
import { useMarketAggregates } from '../contexts/MarketAggregatesContext';
import type { MarketZoneId } from '@app/common/static-data/energy-markets';

interface ZoneSelectorProps {
  selectedZones: MarketZoneId[];
  onZonesChange: (zones: MarketZoneId[]) => void;
}

const ZoneSelector: React.FC<ZoneSelectorProps> = ({ selectedZones, onZonesChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectorRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { state } = useMarketAggregates();

  const selectedZoneLabels = selectedZones
    .map((id) => AVAILABLE_MAP_ZONES.find((z) => z.id === id)?.label || id)
    .join(', ');

  // Compact summary for button label, e.g., "Northern Ireland, +2"
  const selectedSummary = useMemo(() => {
    if (selectedZones.length === 0) return 'Select Zones';
    const labels = selectedZones.map(
      (id) => AVAILABLE_MAP_ZONES.find((z) => z.id === id)?.label || id,
    );
    const first = labels[0];
    const extra = labels.length - 1;
    return extra > 0 ? `${first}, +${extra}` : first;
  }, [selectedZones]);

  // Filter zones based on API response and search term
  const filteredZones = useMemo(() => {
    // If we have available zones from API, filter by those
    if (state.availableZones.length > 0) {
      return AVAILABLE_MAP_ZONES.filter((zone) => {
        const isAvailable = state.availableZones.includes(zone.id);
        const matchesSearch = zone.label.toLowerCase().includes(searchTerm.toLowerCase());
        return isAvailable && matchesSearch;
      }).sort((a, b) => a.label.localeCompare(b.label));
    }

    // If zones are still loading, show empty array to prevent flickering
    if (state.availableZonesLoading) {
      return [];
    }

    // Fallback to all zones only if API failed (no loading, no zones)
    return AVAILABLE_MAP_ZONES.filter((zone) =>
      zone.label.toLowerCase().includes(searchTerm.toLowerCase()),
    ).sort((a, b) => a.label.localeCompare(b.label));
  }, [state.availableZones, state.availableZonesLoading, searchTerm]);

  const focusWithoutScroll = (el: HTMLElement | null | undefined) => {
    if (!el) return;
    try {
      (el as any).focus?.({ preventScroll: true });
    } catch {
      el.focus();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && target.closest('.react-resizable-handle')) {
        return; // ignore resize handle interactions
      }
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        focusWithoutScroll(buttonRef.current || undefined);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const toggleZone = (zone: MarketZoneId) => {
    const isSelected = selectedZones.includes(zone);
    const next = isSelected ? selectedZones.filter((z) => z !== zone) : [...selectedZones, zone];
    onZonesChange(next);
    // keep dropdown open for multi-select usability
    setSearchTerm('');
    focusWithoutScroll(buttonRef.current || undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      focusWithoutScroll(buttonRef.current || undefined);
    }
  };

  return (
    <div className="flex items-center gap-2" ref={selectorRef}>
      <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
        Zones
      </label>
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-3 py-1 text-xs rounded-md transition-colors justify-between whitespace-nowrap ${
            selectedZones.length === 0
              ? 'bg-pink-200 dark:bg-pink-900/20 border border-pink-300 dark:border-pink-800 text-gray-600 dark:text-gray-300 hover:bg-pink-300 dark:hover:bg-pink-900/30 hover:text-gray-900 dark:hover:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
          title={selectedZoneLabels || 'Select Zones'}
        >
          <span className="truncate">{selectedSummary}</span>
          <ChevronDown
            className={`h-3 w-3 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div
            className="absolute z-50 mt-1 w-64 sm:w-80 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg"
            onKeyDown={handleKeyDown}
          >
            {/* Search */}
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <Search className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search zones..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Clear Selection Option */}
            <div className="p-1">
              <div
                onClick={() => onZonesChange([])}
                className={`p-2 text-xs rounded cursor-pointer transition-colors ${
                  selectedZones.length === 0
                    ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Clear Selection
              </div>
            </div>

            {/* Zone List */}
            <div className="max-h-60 overflow-y-auto p-1">
              {state.availableZonesLoading ? (
                <div className="p-2 text-xs text-gray-500 dark:text-gray-400">Loading zones...</div>
              ) : (
                <>
                  {filteredZones.map((zone) => {
                    const isSelected = selectedZones.includes(zone.id as MarketZoneId);
                    return (
                      <div
                        key={zone.id}
                        onClick={() => toggleZone(zone.id as MarketZoneId)}
                        className={`p-2 text-xs rounded cursor-pointer transition-colors flex flex-col ${
                          isSelected
                            ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 font-medium'
                            : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="font-medium">{zone.label}</span>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">{zone.id}</span>
                      </div>
                    );
                  })}
                  {filteredZones.length === 0 && !state.availableZonesLoading && (
                    <div className="p-2 text-xs text-gray-500 dark:text-gray-400">
                      No zones found
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoneSelector;
